import { AiChatMessage, AiChatMessageType } from "@/graphql/__generated__/graphql";
import { cn } from "@/lib/utils";
import { normalizeSources } from "@/pages/EditTender/DocumentChat/utils";
import { motion } from "framer-motion";
import { Source } from "@/pages/EditTender/DocumentChat/Components/SourceCard";
import { TFunction } from "i18next";
import { FC } from "react";
import AssistantMessageWrapper from "./AssistantMessageWrapper";
import { StandaloneMarkdownText } from "./markdown-text";
import UserMessageBubble from "./UserMessageBubble";
import SourcesList from "@/pages/EditTender/DocumentChat/Components/SourceCard";

/**
 * Renders a single message within the document chat thread.
 *
 * - Assistant answers are shown with actions (copy/refresh) and optional sources.
 * - User messages are shown as simple bubbles with markdown support.
 */
interface Props {
  /** Chat message payload including optional pre-normalized sources */
  message: AiChatMessage & { sources?: Source[] };
  /** Returns the previous user message text for resend/refresh actions */
  getPreviousUserMessage?: () => string | undefined;
  /** Triggers a resend of a user query (used by refresh action) */
  onResend: (_: string) => void;
  /** Whether to persist messages (currently unused; reserved) */
  storeMessages?: boolean;
  /** i18n translate function */
  t: TFunction;
}

const MessageRenderer: FC<Props> = ({ message, getPreviousUserMessage = () => undefined, onResend, storeMessages: _storeMessages = false, t }) => {
  // Model responses may include an inline <SOURCE> JSON block. Split answer from source payload.
  const [answerPart, sourcePart] = message.text.split("<SOURCE>");
  // Prefer sources provided directly on the message; fall back to parsing from the inline block.
  let sources: Source[] = Array.isArray((message as any).sources) ? (message as any).sources as Source[] : [];

  if ((!sources || sources.length === 0) && sourcePart) {
    try {
      const rawSources = JSON.parse(sourcePart.replace("</SOURCE>", "").trim());
      sources = normalizeSources(rawSources);
    } catch (e) {
      // If parsing fails, proceed gracefully without sources.
      console.error("Failed to parse sources:", e);
    }
  }

  return (
    <motion.div
      key={message.id}
      initial={{ y: 5, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      // Layout adapts between assistant answers and user messages
      className={cn(
        message.type === AiChatMessageType.Answer ?
          "w-full min-w-0" :
          "grid auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] gap-y-1 [&:where(>*)]:col-start-2 min-w-0"
      )}
    >
      {(message.type === AiChatMessageType.Answer) ? (
        <AssistantMessageWrapper
          t={t}
          copyText={message.text}
          onRefresh={() => {
            // Resend the previous user query to regenerate the assistant answer
            const prev = getPreviousUserMessage();
            if (prev) onResend(prev);
          }}
        >
          {/* Render the markdown-formatted assistant answer body */}
          <StandaloneMarkdownText>{answerPart}</StandaloneMarkdownText>
          {Array.isArray(sources) && sources.length > 0 ? (
            <div className="mt-3">
              {/* Show associated sources (in documents) for the current answer */}
              <SourcesList sources={sources} qaSetId={String(message.id)} />
            </div>
          ) : null}
        </AssistantMessageWrapper>
      ) : (
        // Render user's original message as a bubble
        <UserMessageBubble content={message.text} />
      )}
    </motion.div>
  );
};

export default MessageRenderer;
